import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useXP } from '@/hooks/useXP';
import StatsOverview from './StatsOverview';
import VideoLessonsHub from './VideoLessonsHub';
import SovereignAIEngine from '@/components/ai/SovereignAIEngine';
import ModulePracticeModal from './ModulePracticeModal';
import { TOPICS_DATA, TopicItem } from '@/data/platformTopics';
import { 
  Building2, 
  Briefcase,
  Terminal,
  BookOpen,
  Award,
  ChevronRight,
  TrendingUp,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Zap,
  Layers,
  ArrowUpRight
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] }
  }
};

const DashboardHub = () => {
  const { user, metadata } = useAuth();
  const xpData = useXP(user?.id);
  const navigate = useNavigate();
  const [selectedTopic, setSelectedTopic] = useState<TopicItem | null>(null);

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16 space-y-8"
    >
      
      {/* ═══ HERO BANNER ═══ */}
      <motion.div variants={itemVariants} className="glass-panel p-6 sm:p-10 rounded-3xl space-y-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-60 h-60 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform duration-700" />
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] font-bold text-white uppercase tracking-widest backdrop-blur-md">
              <Sparkles className="w-3 h-3 text-amber-300" />
              <span>Единая кадровая и образовательная экосистема</span>
            </div>
            
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white leading-tight">
              Интеллектуальная среда подготовки кадров и оценки компетенций
            </h1>
            
            <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
              Платформа объединяет обучение, независимый симулятор квалификационного собеседования и корпоративную систему сертификации («UzDigComp-AI») для банковского и IT-сектора.
            </p>
          </div>

          <div className="flex flex-wrap lg:flex-col gap-3 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => navigate('/assessment')}
              className="glass-btn-primary px-5 py-3 rounded-2xl text-xs uppercase tracking-wider flex items-center gap-2.5 shadow-lg"
            >
              <Building2 className="w-4 h-4" />
              <span>Панель работодателя (UzDigComp-AI)</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => navigate('/interview')}
              className="glass-btn-secondary px-5 py-3 rounded-2xl text-xs uppercase tracking-wider flex items-center gap-2.5"
            >
              <Briefcase className="w-4 h-4" />
              <span>Симулятор собеседования</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => navigate('/ide')}
              className="glass-btn-secondary px-5 py-3 rounded-2xl text-xs uppercase tracking-wider flex items-center gap-2.5"
            >
              <Terminal className="w-4 h-4" />
              <span>Среда разработки (IDE)</span>
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-white/10 relative z-10">
          <div className="glass-panel-subtle p-4 rounded-2xl hover:border-white/20 transition-all">
            <div className="text-[10px] text-white/50 uppercase font-semibold">Профессиональная подготовка</div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1">Python, алгоритмы, базы данных</div>
          </div>
          <div className="glass-panel-subtle p-4 rounded-2xl hover:border-white/20 transition-all">
            <div className="text-[10px] text-white/50 uppercase font-semibold">Оценка работодателей</div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1">АКБ «Agrobank», O'zbektelekom</div>
          </div>
          <div className="glass-panel-subtle p-4 rounded-2xl hover:border-white/20 transition-all">
            <div className="text-[10px] text-white/50 uppercase font-semibold">Адаптивный алгоритм CAT</div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1">-48% вопросов без потери точности</div>
          </div>
          <div className="glass-panel-subtle p-4 rounded-2xl hover:border-white/20 transition-all">
            <div className="text-[10px] text-white/50 uppercase font-semibold">Свидетельства DGU</div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1">№ 63640 и № 52469</div>
          </div>
        </div>
      </motion.div>

      {/* ═══ SOVEREIGN AI ENGINE ═══ */}
      <motion.div variants={itemVariants}>
        <SovereignAIEngine />
      </motion.div>

      {/* ═══ STRUCTURED CURRICULUM & COMPETENCIES ═══ */}
      <motion.div variants={itemVariants} className="glass-panel p-6 sm:p-8 rounded-3xl space-y-6">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-purple-400" />
              <span>Учебный план и профессиональные модули</span>
            </div>
            <h2 className="text-xl font-bold text-white mt-0.5">
              Программа прикладных компетенций
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate('/ide')}
            className="glass-btn-secondary px-4 py-2 rounded-xl text-xs uppercase tracking-wider flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <span>Перейти в IDE</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TOPICS_DATA.map((t, idx) => (
            <motion.div
              key={t.id}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedTopic(t)}
              className="glass-panel-subtle p-5 rounded-2xl hover:border-white/40 hover:shadow-xl hover:shadow-purple-950/20 transition-all cursor-pointer flex flex-col justify-between group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/[0.02] rounded-full blur-xl group-hover:bg-white/[0.08] transition-colors" />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase font-bold text-white/40">{t.category}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white font-semibold">
                    {t.level}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors">{t.title}</h3>
                <p className="text-xs text-white/60 mt-1 line-clamp-2 leading-relaxed">{t.description}</p>
              </div>

              <div className="pt-3 mt-3 border-t border-white/10 flex items-center justify-between text-xs font-semibold text-white/70 group-hover:text-white">
                <span>Пройти задания</span>
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ═══ STATS & VIDEO PRACTICUM ═══ */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <StatsOverview stats={xpData.userStats} loading={xpData.loading} />
        </div>
        <div className="lg:col-span-2">
          <VideoLessonsHub />
        </div>
      </motion.div>

      {/* ═══ TOPIC PRACTICE MODAL ═══ */}
      {selectedTopic && (
        <ModulePracticeModal
          topic={selectedTopic}
          onClose={() => setSelectedTopic(null)}
        />
      )}
    </motion.div>
  );
};

export default DashboardHub;
